import React, { useEffect, useRef, useState } from "react";

/* ─────────────────────────────────────────────
   Scroll-reveal hook
───────────────────────────────────────────── */
function useReveal(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function Reveal({ children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 0.8s ease ${delay}ms, transform 0.8s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Data
───────────────────────────────────────────── */

const services = [
  {
    title: "Electrical Engineering",
    desc: "HT/LT installations, substation works, power distribution systems, lighting solutions, earthing & protection systems executed with precision and compliance.",
  },
  {
    title: "MEP Solutions",
    desc: "Integrated Mechanical, Electrical & Plumbing systems including HVAC, fire-fighting, water supply, drainage, and building automation.",
  },
  {
    title: "Fire Fighting Systems",
    desc: "Design, supply & installation of fire alarm systems, hydrant networks, sprinkler systems, extinguishers and complete statutory fire safety compliance solutions.",
  },
  {
    title: "Civil Finishing Works",
    desc: "High-quality interior & exterior civil finishing, structural coordination, and turnkey fit-out execution.",
  },
  {
    title: "Turnkey Projects",
    desc: "Complete project lifecycle management — design, engineering, procurement, construction, installation, testing & commissioning.",
  },
  {
    title: "PMC & Site Supervision",
    desc: "Project Management Consultancy, technical supervision, vendor coordination and execution monitoring for large-scale developments.",
  },
];

const executionPhases = [
  "Concept & Feasibility",
  "Detailed Engineering",
  "Procurement & Vendor Coordination",
  "Construction & Installation",
  "Testing & Commissioning",
  "Operations & Maintenance",
];

const fontLink =
  "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500&display=swap";

export default function Services() {

  useEffect(() => {
    if (!document.querySelector(`link[href="${fontLink}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = fontLink;
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div
      className="bg-white text-gray-800 overflow-x-hidden"
      style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 300 }}
    >

      <style>{`
        @keyframes heroFadeUp {
          from { opacity: 0; transform: translateY(28px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .service-card:hover { border-color:#ea580c; background:#fff7f0; }
        .service-card:hover .service-title { color:#ea580c; }
        .phase-item:hover .phase-bar { height:30px; }
      `}</style>

      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <section className="relative min-h-[380px] w-full overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-50/30">

        <div className="flex flex-col justify-center items-center px-8 lg:px-20 min-h-[380px] text-center">
          <div
            style={{ animation: "heroFadeUp 0.9s ease 0.2s both" }}
            className="flex items-center gap-3 mb-4"
          >
            <span className="w-8 h-px bg-orange-500" />
            <span className="text-orange-600 text-[11px] tracking-[0.28em] uppercase" style={{ fontWeight: 500 }}>
              Engineering Capabilities
            </span>
            <span className="w-8 h-px bg-orange-500" />
          </div>

          <h1
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 400,
              animation: "heroFadeUp 1s ease 0.4s both",
            }}
            className="text-5xl md:text-6xl lg:text-7xl leading-[1.1] text-gray-900 mb-5"
          >
            Our{" "}
            <em className="text-orange-500" style={{ fontStyle: "italic" }}>
              Services
            </em>
          </h1>

          <p
            style={{
              fontWeight: 300,
              maxWidth: 620,
              animation: "heroFadeUp 1s ease 0.6s both",
            }}
            className="text-gray-600 text-[15px] leading-loose"
          >
            Integrated Electrical, MEP, Civil Finishing & Solar EPC solutions delivered with precision, compliance and performance excellence.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════
          SERVICES GRID
      ══════════════════════════════════════ */}
      <section className="py-16 px-6 lg:px-20 max-w-7xl mx-auto">

        <Reveal className="text-center mb-12">
          <p className="text-orange-500 text-[11px] tracking-[0.32em] uppercase mb-4 flex items-center justify-center gap-3" style={{ fontWeight: 500 }}>
            <span className="w-6 h-px bg-orange-400" />
            What We Deliver
            <span className="w-6 h-px bg-orange-400" />
          </p>

          <h2
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
            className="text-4xl md:text-5xl text-gray-900"
          >
            Comprehensive{" "}
            <em style={{ fontStyle: "italic" }} className="text-orange-500">
              Engineering Solutions
            </em>
          </h2>

          <div className="w-14 h-[3px] bg-orange-500 mx-auto mt-6" />
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
          {services.map((service, i) => (
            <Reveal key={i} delay={i * 80} className="h-full">
              <div className="service-card border border-gray-100 p-8 transition-all duration-300 h-full flex flex-col">

                <h3
                  style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 600 }}
                  className="service-title text-2xl text-gray-900 mb-4 leading-snug transition-colors"
                >
                  {service.title}
                </h3>

                <p
                  style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 400 }}
                  className="text-[15px] leading-[1.8] text-gray-600 flex-grow"
                >
                  {service.desc}
                </p>

              </div>
            </Reveal>
          ))}
        </div>

        {/* ══════════════════════════════════════
            EXECUTION PHASES
        ══════════════════════════════════════ */}
        <div className="mt-20">

          <Reveal>
            <div className="max-w-3xl mx-auto">
              <h3
                style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
                className="text-4xl text-gray-900 mb-8 text-center"
              >
                Our Execution{" "}
                <em className="text-orange-500" style={{ fontStyle: "italic" }}>
                  Methodology
                </em>
              </h3>

              <p className="text-[15px] leading-[2] text-gray-600 mb-10 text-center">
                ATPPL delivers projects through a structured lifecycle approach
                ensuring quality, safety, compliance and timely execution at
                every stage.
              </p>

              <ul className="bg-gradient-to-br from-orange-50/50 to-transparent border border-orange-100/50 p-8 rounded-sm">
                {executionPhases.map((phase, i) => (
                  <li key={i} className="phase-item flex items-center gap-5 py-4 border-b border-gray-100 last:border-b-0">
                    <span
                      className="phase-bar w-[3px] h-5 bg-orange-500 flex-shrink-0 transition-all duration-300"
                    />
                    <span className="text-gray-600 text-[14px]">
                      {phase}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

        </div>

        {/* ══════════════════════════════════════
            COMMITMENT SECTION
        ══════════════════════════════════════ */}
        <Reveal className="mt-20">
          <div className="text-center max-w-4xl mx-auto px-10 py-16 bg-orange-50 border border-orange-100">

            <p className="text-orange-500 text-[11px] tracking-[0.32em] uppercase mb-5 flex items-center justify-center gap-3" style={{ fontWeight: 500 }}>
              <span className="w-6 h-px bg-orange-400" />
              Performance Commitment
              <span className="w-6 h-px bg-orange-400" />
            </p>

            <h3
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
              className="text-4xl text-gray-900 mb-8"
            >
              Precision. Safety.{" "}
              <em className="text-orange-500" style={{ fontStyle: "italic" }}>
                Reliability.
              </em>
            </h3>

            <p className="text-[15px] leading-[2] text-gray-600 mb-8">
              Backed by ISO 9001:2015 Quality Systems and ISO 45001:2018
              Occupational Health & Safety standards, we ensure disciplined
              project control, risk mitigation, and long-term operational
              performance.
            </p>

            <p
              style={{
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                fontWeight: 400,
              }}
              className="text-[22px] text-gray-800"
            >
              "We engineer systems that perform — today and for decades ahead."
            </p>

          </div>
        </Reveal>

      </section>

      {/* FOOTER STRIP */}
      <div className="bg-orange-600 text-white text-center py-7 tracking-[0.25em] text-[13px] uppercase" style={{ fontWeight: 600 }}>
        Delivering Integrated Engineering Excellence.
      </div>

    </div>
  );
}